// other — control centre shell (Business UI handoff, 2026-10-07): the same
// frame as the workflow app — a 216px white sidebar (logo, "Control centre",
// the hub's tools, the active one in black) and a 68px top bar with who is
// signed in. Workflow and the client portal are separate apps; the sidebar
// links to their hub views, which link out.
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu as MenuIcon, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";
import { canSeeSection, type HubSection } from "@/lib/access";

// Each item shows only to the roles that may open it (lib/access.ts, CEO
// 2026-10-21: Marketing → admin, marketing, finance).
const navItems: Array<{ to: string; label: string; end?: boolean; section: HubSection }> = [
  { to: "/", label: "Home", end: true, section: "home" },
  { to: "/products", label: "Products", section: "products" },
  { to: "/studio", label: "Studio", section: "studio" },
  { to: "/website", label: "Website", section: "website" },
  { to: "/workflow", label: "Flow", section: "workflow" },
  { to: "/marketing", label: "Marketing", section: "marketing" },
  { to: "/finance", label: "Finance", section: "finance" },
];

export function Shell() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const nav = (
    <nav aria-label="Control centre" className="flex flex-col gap-[3px]">
      <p className="mx-3 mb-2 mt-6 text-[11px] uppercase tracking-[0.12em] text-[#777]">Control centre</p>
      {navItems.filter((item) => canSeeSection(user?.roles, item.section)).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            cn(
              "flex min-h-[40px] items-center rounded px-3 text-[14px] transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-focus",
              isActive ? "bg-ink text-white" : "text-ink hover:bg-paper",
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-paper text-[14px]">
      {open && <div className="fixed inset-0 z-30 bg-ink/30 md:hidden" onClick={() => setOpen(false)} aria-hidden />}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-[216px] flex-col border-r border-rule bg-white px-[18px] py-[30px] transition-transform md:static md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-start justify-between px-3">
          <Link to="/" aria-label="other — control centre home">
            <Logo />
          </Link>
          <button type="button" className="md:hidden" aria-label="Close menu" onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        {nav}
        <p className="mt-auto px-3 pt-8 text-xs text-ink-muted">
          <span className="block font-medium text-ink">Good work. Together.</span>
          other · hub
        </p>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[68px] items-center gap-5 border-b border-rule bg-white px-4 md:px-8">
          <button type="button" className="md:hidden" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
            <MenuIcon className="h-5 w-5" />
          </button>
          <span className="text-[13px] text-ink-muted">Operations</span>
          <div className="ml-auto flex items-center gap-4">
            {user && (
              <span className="hidden text-right text-xs sm:block">
                <b className="block font-semibold text-ink">{user.name}</b>
                <span className="text-ink-muted">Control centre</span>
              </span>
            )}
            <button onClick={onLogout} className="inline-flex min-h-[40px] items-center gap-1.5 rounded border border-rule bg-white px-3.5 text-[13px] text-ink hover:bg-paper" type="button">
              <LogOut className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              Sign out
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
